const { trialEndsAt } = require("./performanceTrial")
const quietPeriodMs = 1500

const normalisePath = (pathname = "") =>
  String(pathname).replace(/\/$/, "") || "/"

function installHubSpotChatDelay(
  browser,
  { now = () => Date.now(), endsAt = trialEndsAt } = {}
) {
  if (!browser) return null
  if (browser.__expendHubSpotChatDelay) return browser.__expendHubSpotChatDelay

  let pathname = normalisePath(browser.location?.pathname)
  let formReady = Boolean(browser.__expendHubSpotFormReadySent)
  let interacted = false
  let quiet = false
  let scheduled = false
  let widgetLoadQueued = false
  let delayWasEnabled = false
  let idleId
  let quietTimerId
  let fallbackTimerId
  let expiryTimerId

  const interactionEvents = [
    "pointerdown",
    "pointermove",
    "touchstart",
    "keydown",
    "wheel",
  ]
  const trialIsActive = () => now() < endsAt

  const removeInteractionListeners = () => {
    for (const event of interactionEvents)
      browser.removeEventListener(event, onInteraction, true)
  }

  const loadWidget = () => {
    widgetLoadQueued = false
    const load = browser.HubSpotConversations?.widget?.load
    if (typeof load !== "function") return false
    load.call(browser.HubSpotConversations.widget)
    return true
  }

  const loadWidgetWhenReady = () => {
    if (browser.__expendHubSpotChatReleased) return
    browser.__expendHubSpotChatReleased = true
    removeInteractionListeners()
    if (loadWidget() || widgetLoadQueued) return
    widgetLoadQueued = true
    browser.hsConversationsOnReady = browser.hsConversationsOnReady || []
    browser.hsConversationsOnReady.push(loadWidget)
  }

  const cancelScheduled = () => {
    scheduled = false
    if (idleId !== undefined) browser.cancelIdleCallback?.(idleId)
    if (quietTimerId !== undefined) browser.clearTimeout(quietTimerId)
    if (fallbackTimerId !== undefined) browser.clearTimeout(fallbackTimerId)
    idleId = undefined
    quietTimerId = undefined
    fallbackTimerId = undefined
  }

  const release = () => {
    scheduled = false
    idleId = undefined
    fallbackTimerId = undefined
    if (pathname !== "/get-started" || !formReady || !interacted || !quiet)
      return
    loadWidgetWhenReady()
  }

  const schedule = () => {
    if (
      scheduled ||
      pathname !== "/get-started" ||
      browser.__expendHubSpotChatReleased ||
      !formReady ||
      !interacted ||
      !quiet
    )
      return

    scheduled = true
    if (browser.requestIdleCallback)
      idleId = browser.requestIdleCallback(release, { timeout: 2000 })
    else fallbackTimerId = browser.setTimeout(release, 250)
  }

  const afterQuietPeriod = () => {
    quietTimerId = undefined
    quiet = true
    schedule()
  }

  function onInteraction(event) {
    if (
      pathname !== "/get-started" ||
      browser.__expendHubSpotChatReleased ||
      !event?.isTrusted
    )
      return
    interacted = true
    quiet = false
    if (quietTimerId !== undefined) browser.clearTimeout(quietTimerId)
    if (scheduled) {
      scheduled = false
      if (idleId !== undefined) browser.cancelIdleCallback?.(idleId)
      if (fallbackTimerId !== undefined) browser.clearTimeout(fallbackTimerId)
      idleId = undefined
      fallbackTimerId = undefined
    }
    quietTimerId = browser.setTimeout(afterQuietPeriod, quietPeriodMs)
  }

  const enableDelay = () => {
    if (pathname !== "/get-started" || !trialIsActive()) return
    delayWasEnabled = true
    browser.hsConversationsSettings = browser.hsConversationsSettings || {}
    browser.hsConversationsSettings.loadImmediately = false
    if (expiryTimerId === undefined) {
      const remaining = Math.max(0, endsAt - now())
      expiryTimerId = browser.setTimeout(() => {
        expiryTimerId = undefined
        if (browser.hsConversationsSettings)
          browser.hsConversationsSettings.loadImmediately = true
        loadWidgetWhenReady()
      }, Math.min(remaining, 2147483647))
    }
  }

  const controller = {
    onFormReady() {
      formReady = true
      schedule()
    },
    onNavigation(nextPathname) {
      pathname = normalisePath(nextPathname || browser.location?.pathname)
      if (pathname !== "/get-started") {
        cancelScheduled()
        if (expiryTimerId !== undefined) browser.clearTimeout(expiryTimerId)
        expiryTimerId = undefined
        if (delayWasEnabled) {
          if (browser.hsConversationsSettings?.loadImmediately === false)
            browser.hsConversationsSettings.loadImmediately = true
          loadWidgetWhenReady()
        }
        return
      }
      formReady = formReady || Boolean(browser.__expendHubSpotFormReadySent)
      enableDelay()
      schedule()
    },
    dispose() {
      cancelScheduled()
      if (expiryTimerId !== undefined) browser.clearTimeout(expiryTimerId)
      removeInteractionListeners()
      if (browser.__expendHubSpotChatDelay === controller)
        delete browser.__expendHubSpotChatDelay
    },
  }

  browser.__expendHubSpotChatDelay = controller
  for (const event of interactionEvents)
    browser.addEventListener(event, onInteraction, {
      capture: true,
      passive: event !== "keydown",
    })
  enableDelay()
  schedule()
  return controller
}

module.exports = { installHubSpotChatDelay, quietPeriodMs, trialEndsAt }
